import { useState, useEffect } from 'react'
import type { Supplier, UpdateSupplierData } from '@/types'

interface SupplierEditFormProps {
  supplier: Supplier
  onSave: (data: UpdateSupplierData) => void
  onCancel: () => void
  isSaving: boolean
}

export function SupplierEditForm({
  supplier,
  onSave,
  onCancel,
  isSaving,
}: SupplierEditFormProps) {
  const [name, setName] = useState(supplier.name)
  const [alternativeNames, setAlternativeNames] = useState<string[]>(
    supplier.alternative_names || []
  )
  const [metadataJson, setMetadataJson] = useState(
    JSON.stringify(supplier.metadata || {}, null, 2)
  )
  const [newAltName, setNewAltName] = useState('')
  const [jsonError, setJsonError] = useState<string | null>(null)

  useEffect(() => {
    setName(supplier.name)
    setAlternativeNames(supplier.alternative_names || [])
    setMetadataJson(JSON.stringify(supplier.metadata || {}, null, 2))
  }, [supplier])

  const handleAddAltName = () => {
    const trimmed = newAltName.trim()
    if (trimmed && !alternativeNames.includes(trimmed)) {
      setAlternativeNames([...alternativeNames, trimmed])
      setNewAltName('')
    }
  }

  const handleRemoveAltName = (index: number) => {
    setAlternativeNames(alternativeNames.filter((_, i) => i !== index))
  }

  const handleMetadataChange = (value: string) => {
    setMetadataJson(value)
    try {
      JSON.parse(value)
      setJsonError(null)
    } catch {
      setJsonError('Invalid JSON format')
    }
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    if (jsonError) return

    let metadata: Record<string, unknown> = {}
    try {
      metadata = JSON.parse(metadataJson)
    } catch {
      setJsonError('Invalid JSON format')
      return
    }

    onSave({
      name: name.trim(),
      alternative_names: alternativeNames,
      metadata,
    })
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6" data-testid="supplier-edit-form">
      {/* Name field */}
      <div>
        <label htmlFor="name" className="block text-sm font-medium text-gray-700">
          Supplier Name
        </label>
        <input
          type="text"
          id="name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
          required
          maxLength={255}
          data-testid="name-input"
        />
      </div>

      {/* Alternative names */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Alternative Names
        </label>
        <div className="space-y-2">
          {alternativeNames.map((altName, index) => (
            <div
              key={index}
              className="flex items-center gap-2 bg-gray-50 px-3 py-2 rounded-md"
            >
              <span className="flex-1 text-sm">{altName}</span>
              <button
                type="button"
                onClick={() => handleRemoveAltName(index)}
                className="text-red-500 hover:text-red-700"
                data-testid={`remove-alt-name-${index}`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          ))}
          <div className="flex gap-2">
            <input
              type="text"
              value={newAltName}
              onChange={(e) => setNewAltName(e.target.value)}
              placeholder="Add alternative name..."
              className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  handleAddAltName()
                }
              }}
              data-testid="new-alt-name-input"
            />
            <button
              type="button"
              onClick={handleAddAltName}
              className="px-3 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
              data-testid="add-alt-name-btn"
            >
              Add
            </button>
          </div>
        </div>
      </div>

      {/* Metadata JSON editor */}
      <div>
        <label htmlFor="metadata" className="block text-sm font-medium text-gray-700">
          Metadata (JSON)
        </label>
        <textarea
          id="metadata"
          value={metadataJson}
          onChange={(e) => handleMetadataChange(e.target.value)}
          rows={6}
          className={`mt-1 block w-full rounded-md shadow-sm sm:text-sm font-mono ${
            jsonError
              ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
              : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
          }`}
          data-testid="metadata-input"
        />
        {jsonError && (
          <p className="mt-1 text-sm text-red-600" data-testid="json-error">
            {jsonError}
          </p>
        )}
      </div>

      {/* Action buttons */}
      <div className="flex justify-end gap-3">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
          data-testid="cancel-btn"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isSaving || !!jsonError}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
          data-testid="save-btn"
        >
          {isSaving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </form>
  )
}
